// src/components/RenderMesas.tsx
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Mesa, Salon } from '../../../../../types';
import './RenderMesas.scss';
import { toast } from 'react-toastify';
import { FaCrown, FaUser, FaDollarSign } from 'react-icons/fa';
import { ReservationDetails, DisplayPrefs } from '../../../../../../Pedidos/Restaurant/types';
import CancelMerge from './components/CancelMerge/CancelMerge';
import { formatCurrency, getPermissionState } from '../../../../../../../../utils/functions';

interface RenderMesasProps {
  salon: Salon | undefined;
  actionType: string; // 'restaurant' | 'reservation' | 'configuration'
  scaleFactor: number;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, item: Mesa) => void;
  setSelectedItem: (item: Mesa) => void;
  selectedItem: any;
  isPedidos: boolean;
  mergeMode: boolean;
  mergedMesas: Mesa[];
  setMergedMesas: (mesas: Mesa[]) => void;
  filteredReservationsInAHour?: ReservationDetails[];
  setPxPerCellX: (pxPerCellX: number) => void;
  setPxPerCellY: (pxPerCellY: number) => void;
  displayPrefs?: DisplayPrefs;
}

const RenderMesas: React.FC<RenderMesasProps> = ({
  salon,
  actionType,
  scaleFactor,
  onDragStart,
  selectedItem,
  setSelectedItem,
  isPedidos,
  mergeMode,
  mergedMesas,
  setMergedMesas,
  filteredReservationsInAHour = [],
  setPxPerCellX,
  setPxPerCellY,
  displayPrefs,
}) => {


  const [showCancelMergeModal, setShowCancelMergeModal] = useState(false);
  const [selectedMesaId, setSelectedMesaId] = useState<number | null>(null);
  const mesas = useMemo(() => salon?.mesas || [], [salon]);

  const [timePassed, setTimePassed] = useState<Record<number, string>>({});
  const [expandedMesaNameId, setExpandedMesaNameId] = useState<number | null>(null);
  const selectedItemRef = useRef<HTMLDivElement>(null);

  const formatTimeElapsed = (timeUsed: string) => {
    if (!timeUsed) return '';
    const now = Date.now();
    const usedAt = new Date(timeUsed).getTime();
    if (isNaN(usedAt)) return '';
    let diff = now - usedAt;
    const sign = diff < 0 ? '-' : '';
    diff = Math.abs(diff);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    // Siempre forzar formato: 2 H 25 M (sin plural, sin texto largo)
    return `${sign}${hours} H ${minutes} M`;
  };

  const handleClickMergeIcon = (e: React.MouseEvent, mesaId: number) => {
    e.stopPropagation();
    setSelectedMesaId(mesaId);
    setShowCancelMergeModal(true);
  };

  useEffect(() => {
    const interval = window.setInterval(() => {
      const updated: Record<number, string> = {};
      mesas.forEach(mesa => {
        if (mesa.used && mesa.time_used) {
          updated[mesa.id] = formatTimeElapsed(mesa.time_used);
        }
      });
      setTimePassed(updated);
    }, 60_000);
    return () => clearInterval(interval);
  }, [mesas]);

  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.attendant-name')) return;
      setExpandedMesaNameId(null);
    };
    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, []);

  const checkAlarms = (mesa: Mesa) => {
    // Solo mostrar iconos si la mesa está en uso
    // Removed the snooze icon for "show more" - now handled by CSS border animation
    return null;
  };

  const shouldShowOfferMore = (mesa: Mesa) => {
    if (!mesa.used) return false;
    const now = Date.now();
    if (mesa.time_last_order) {
      const lastOrderAt = new Date(mesa.time_last_order).getTime();
      if (!isNaN(lastOrderAt) && (now - lastOrderAt) / 60000 > 20) {
        return true;
      }
    }
    return false;
  };

  const shouldShowTooLongAtTable = (mesa: Mesa) => {
    if (!mesa.used) return false;
    const now = Date.now();
    if (mesa.time_used) {
      const usedAt = new Date(mesa.time_used).getTime();
      if (!isNaN(usedAt) && (now - usedAt) / 3600000 > 2) {
        return true;
      }
    }
    return false;
  };

  const shouldShowPendingCharge = (mesa: Mesa) => {
    if (!mesa.used) return false;
    const now = Date.now();
    if (mesa.charged && mesa.time_charged) {
      const chargedAt = new Date(mesa.time_charged).getTime();
      if (!isNaN(chargedAt) && (now - chargedAt) / 60000 > 10) {
        return true;
      }
    }
    return false;
  };

  interface MergeGroupInfo { color: string; isMaster: boolean; }
  const mergeGroupMap: Record<number, MergeGroupInfo> = {};
  const COLORS = ['#F44336', '#2196F3', '#4CAF50', '#9C27B0', '#FFEB3B', '#00BCD4'];
  const masters = mesas.filter(m => m.is_merged && m.parent_mesa_id === null);
  masters.forEach((m, i) => {
    mergeGroupMap[m.id] = { color: COLORS[i % COLORS.length], isMaster: true };
  });
  mesas.forEach(m => {
    if (m.is_merged && m.parent_mesa_id != null && mergeGroupMap[m.parent_mesa_id]) {
      mergeGroupMap[m.id] = {
        color: mergeGroupMap[m.parent_mesa_id].color,
        isMaster: false
      };
    }
  });

  const handleMesaClick = (mesa: Mesa) => {
    // Only allow opening mesa modal for users with permission 71
    if (!getPermissionState(71) && !mergeMode) {
      toast.warn('No tienes permisos para tomar pedidos en mesas');
      return;
    }

    if (mergeMode) {
      if (mesa.charged || mesa.state === false) return;
      // Permitir seleccionar mesas que están unidas (is_merged)
      const already = mergedMesas.some(m => m.id === mesa.id);
      setMergedMesas(already
        ? mergedMesas.filter(m => m.id !== mesa.id)
        : [...mergedMesas, mesa]
      );
    } else {
      setSelectedItem(mesa);
    }
  };

  useEffect(() => {
    if (selectedItemRef.current && selectedItem) {
      const { width: pxW, height: pxH } = selectedItemRef.current.getBoundingClientRect();
      setPxPerCellX(pxW / selectedItem.width);
      setPxPerCellY(pxH / selectedItem.height);
    }
  }, [selectedItem, setPxPerCellX, setPxPerCellY]);

  const hasActiveMergedChildren = (masterId: number) =>
  mesas.some(m =>
    m.is_merged &&
    m.parent_mesa_id === masterId &&
    !m.charged &&           
    m.state !== false
  );

  // Check if any mesa in a merge group is charged
  // If so, all mesas in that group should show charged styling
  const isMergeGroupCharged = (mesa: Mesa): boolean => {
    if (!mesa.is_merged) return false;
    
    // Determine the master ID for this merge group
    const masterId = mesa.parent_mesa_id === null ? mesa.id : mesa.parent_mesa_id;
    
    // Find the master mesa
    const masterMesa = mesas.find(m => m.id === masterId);
    if (masterMesa?.charged) return true;
    
    // Check if any child in this merge group is charged
    return mesas.some(m => 
      m.is_merged && 
      m.parent_mesa_id === masterId && 
      m.charged
    );
  };

  // Filter out charged tables and virtual tables when in merge mode
  const mesasToRender = mergeMode 
    ? mesas.filter(mesa => !mesa.charged && !mesa.is_virtual)
    : mesas;

  return (
    <>
      {mesasToRender.map(mesa => {
        const isForMerge = mergedMesas.some(m => m.id === mesa.id);
        const isSel = selectedItem?.id === mesa.id;
        // Considerar mesa reservada como desactivada
        const matchingReservations = filteredReservationsInAHour.filter(r =>
          r.mesa_id === mesa.id &&
          r.approved && r.confirmed && !r.cancelled && !r.arrived && !r.attended
        );
        const isReserved = matchingReservations.length > 0;

        const showOfferMore = shouldShowOfferMore(mesa);
        const showTooLongAtTable = shouldShowTooLongAtTable(mesa);
        const showPendingCharge = shouldShowPendingCharge(mesa);
        
        // Determine base class for virtual tables
        // Virtual tables can be in use (used) or not in use (unused)
        const getVirtualTableClass = () => {
          if (!mesa.is_virtual) return null;
          return mesa.used ? 'virtual-used' : 'virtual-unused';
        };
        
        const classes = [
          'mesa',
          mesa.is_virtual ? (getVirtualTableClass() || 'virtual') : (mesa.used ? 'used' : 'unused'),
          mergeMode && !isForMerge ? 'blurred' : '',
          isForMerge ? 'merge-selected' : '',
          !mergeMode && isSel ? 'selected' : '',
          // FIXED: Separate disabled (state=false) from reserved (has reservations)
          mesa.state === false ? 'disabled' : '',
          isReserved ? 'reserved' : '',
          showOfferMore ? 'offer-more' : '',
          showTooLongAtTable ? 'too-long-at-table' : '',
          showPendingCharge ? 'pending-charge' : '',
          // Apply charged class if this mesa is charged OR if any mesa in its merge group is charged
          (mesa.charged || isMergeGroupCharged(mesa)) ? 'charged' : ''
        ].filter(Boolean).join(' ');

        // outer container uses scaled position and size
        const style: React.CSSProperties = {
          left:   `calc(${mesa.x_pos * scaleFactor}rem * var(--salon-scale-compensation, 1.185))`,
          top:    `calc(${mesa.y_pos * scaleFactor}rem * var(--salon-scale-compensation, 1.185))`,
          width:  `${mesa.width * scaleFactor}px`,
          height: `${mesa.height * scaleFactor}px`,
        };

        const mergeInfo = mergeGroupMap[mesa.id];

        const tooltipMessage = showOfferMore 
          ? "¡Ofrecer más!" 
          : showTooLongAtTable 
            ? "¡+2h en mesa!" 
            : showPendingCharge
              ? "¡Cobro pendiente!"
              : undefined;

        return (
          <div
            ref={isSel ? selectedItemRef : null}
            key={mesa.id}
            draggable
            onDragStart={e => onDragStart(e, mesa)}
            className={classes}
            onClick={() => handleMesaClick(mesa)}
            style={style}
            title={tooltipMessage}
          >
            {/* alert icon, scaled but free positioned */}
            {isPedidos && (
              <div className="alert-container">
                <div
    style={{
      position: 'absolute',
      top: 0,
      right: 0,
      transform: `scale(${scaleFactor})`,
      transformOrigin: 'top right'
    }}
                >
                  {checkAlarms(mesa)}
                </div>
                                {isPedidos && displayPrefs?.showWaiter !== false && mesa.used && mesa.user_name && (
                  <div
                    className={`attendant-name ${
                      expandedMesaNameId === mesa.id ? 'expanded' : ''
                    }`}
                    title={mesa.user_name}
                    onClick={e => {
                      e.stopPropagation();
                      setExpandedMesaNameId(mesa.id);
                    }}
                                      style={{
                    transform: `scale(${scaleFactor})`
                  }}
                  >
                    {expandedMesaNameId === mesa.id
                      ? mesa.user_name
                      : mesa.user_name.charAt(0)}
                  </div>
                )}
                              {mergeInfo && (!mergeInfo.isMaster || hasActiveMergedChildren(mesa.id)) && (
                <div
                  className="merge-marker"
                  onClick={e => { e.stopPropagation(); handleClickMergeIcon(e, mesa.id); }}
                  style={{ backgroundColor: mergeInfo.color, transform: `scale(${scaleFactor})` }}
                  title={mergeInfo.isMaster ? 'Master' : 'Merged'}
                >
                  {mergeInfo.isMaster && <FaCrown className="merge-crown" />}
                </div>
              )}
              
              {/* Charged indicator - Dollar sign when receipt has been generated (show for all mesas in merge group) */}
              {(mesa.charged || isMergeGroupCharged(mesa)) && (
                <div
                  className="charged-indicator"
                  style={{ transform: `scale(${scaleFactor})` }}
                  title="Precuenta generada"
                >
                  <FaDollarSign className="charged-icon" />
                </div>
              )}
              </div>
            )}

            {/* content wrapper: flex center + scale */}
            <div
              className="mesa-inner"
              style={{
                transform: `scale(${scaleFactor})`,
                transformOrigin: 'center center'
              }}
            >


              <div className="mesa-info">


                <div
                  className={
                    `number-capacity${matchingReservations.length > 0 ? ' reserved' : ''}${mesa.tag && mesa.tag.toString().trim() ? ' with-tag' : ''}`
                  }
                >
                  {displayPrefs?.showTag !== false || mesa.is_virtual ? (
                    // Show tag area: custom tag for tagged tables, or default display for others
                    // For virtual tables, ALWAYS show tag regardless of displayPrefs
                    <>
                      {mesa.is_virtual ? (
                        // Virtual table: show tag or "MV" fallback
                        <>
                          <div className="mesa-tag-primary" title={`Mesa ${mesa.num}${mesa.tag && mesa.tag.toString().trim() ? ` - ${mesa.tag}` : ''}`}>
                            {mesa.tag && mesa.tag.toString().trim() ? mesa.tag : 'MV'}
                          </div>
                          {displayPrefs?.showNumber !== false && (
                            <div className="mesa-number-secondary">
                              {mesa.num}
                            </div>
                          )}
                        </>
                      ) : mesa.tag && mesa.tag.toString().trim() ? (
                        // Regular table with custom tag
                        <>
                          <div className="mesa-tag-primary" title={`Mesa ${mesa.num} - ${mesa.tag}`}>
                            {mesa.tag}
                          </div>
                          {displayPrefs?.showNumber !== false && (
                            <div className="mesa-number-secondary">
                              {mesa.num}
                            </div>
                          )}
                        </>
                      ) : (
                        // Regular table without tag: when "Mostrar etiquetas" está activo
                        // mostramos el número como fallback aunque la opción showNumber esté desactivada.
                        // Esto satisface la petición: al activar "Mostrar etiquetas de mesa",
                        // las mesas sin tag deben mostrar su número.
                        <div className="mesa-number-primary" title={`Mesa ${mesa.num}`}>
                          {mesa.num}
                        </div>
                      )}
                    </>
                  ) : (
                    // When showTag is false: large prominent number (only if showNumber and NOT virtual)
                    displayPrefs?.showNumber !== false ? (
                      <div className="mesa-number-primary" title={`Mesa ${mesa.num}`}>
                        {mesa.num}
                      </div>
                    ) : null
                  )}
                  
                  {/* People/Capacity Display - conditional based on showPeopleType and NOT for virtual tables */}
                  {!mesa.is_virtual && (
                    displayPrefs?.showPeopleType === 'current' ? (
                      // Show current_people only if > 0
                      mesa.current_people && mesa.current_people > 0 ? (
                        <div className="capacity">
                          <FaUser className="capacity-icon" />
                          <span className="capacity-text">{mesa.current_people}</span>
                        </div>
                      ) : null
                    ) : (
                      // Show capacity (default behavior)
                      <div className="capacity">
                        <FaUser className="capacity-icon" />
                        <span className="capacity-text">{mesa.people}</span>
                      </div>
                    )
                  )}
                </div>

                {isPedidos && displayPrefs?.showElapsed !== false && !mesa.is_virtual && mesa.used && mesa.time_used && matchingReservations.length === 0 && (
                  <div className="time-elapsed">{timePassed[mesa.id] ?? formatTimeElapsed(mesa.time_used)}</div>
                )}

                {matchingReservations.length > 0 && (
                  <div className="mesa-reservada-msg"><div>Reservado</div></div>
                )}

              </div>
            </div>

            {/* Charges pill: render outside scaled inner, but within mesa to anchor to top border */}
            {displayPrefs?.showCharges !== false && typeof (mesa as any).total_charges === 'number' && (mesa as any).total_charges > 0 && (
              <div className="mesa-charges" title="Total cobrado en esta mesa">
                {formatCurrency((mesa as any).total_charges as number)}
              </div>
            )}
          </div>
        );
      })}

      <CancelMerge
        show={showCancelMergeModal}
        onHide={() => setShowCancelMergeModal(false)}
        mesaId={selectedMesaId || 0}
      />
    </>
  );
};

export default RenderMesas;
